"use client";
import { useCallback, useEffect } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import { DadJokeEntity, SavedJokeEntity } from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import { authClient } from "@constructr-sdk/auth/client";
import type {
  UseDadJokesParams,
  UseDadJokesReturn,
} from "~/modules/main/hooks/use-dad-jokes/use-dad-jokes.interface";

// Global state atoms - hoisted to top level to prevent recreation
const currentJokeAtom = atom<DadJokeEntity | null>(null);
const errorMessageAtom = atom<string | null>(null);
const seenJokesAtom = atom<string[]>([]);

/**
 * Normalizes a joke text by trimming and collapsing whitespace.
 * This ensures robust comparison for exact duplicates.
 */
const normalizeJoke = (text: string): string => {
  return text.replace(/\s+/g, ' ').trim();
};

export function useDadJokes(params: UseDadJokesParams): UseDadJokesReturn {
  const { autoLoadLibrary = false } = params;

  // Global state management
  const [currentJoke, setCurrentJoke] = useAtom(currentJokeAtom);
  const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom);
  const [seenJokes, setSeenJokes] = useAtom(seenJokesAtom);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Authentication state
  const { data: session } = authClient.useSession();
  const isAuthenticated = !!session?.user;

  // Query for saved jokes
  const {
    data: savedJokesData,
    isLoading: isLoadingLibrary,
    refetch: refetchSavedJokes,
  } = trpcReactQueryApi.main.dadJokes.getSavedJokes.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Mutation for generating a new joke with exclusion list
  const generateJokeMutation =
    trpcReactQueryApi.main.dadJokes.generateJoke.useMutation({
      onSuccess: (data) => {
        const normalizedNewJoke = normalizeJoke(data.text);
        
        // Check if this joke was already seen in the session (edge case)
        if (seenJokes.includes(normalizedNewJoke)) {
          const message = "We couldn't find a new one this time. Try again!";
          setErrorMessage(message);
          toast.error(message);
          return;
        }

        // Update current joke and add to seen list
        setCurrentJoke(data);
        setErrorMessage(null);
        
        // Add to seen jokes list, capping at 100 entries to avoid unbounded growth
        setSeenJokes((prev) => {
          const updated = [...prev, normalizedNewJoke];
          return updated.slice(-100);
        });
        
        toast.success("New Dad joke generated!");
      },
      onError: (error) => {
        const message = error.message || "Couldn't generate a new unique Dad joke. Please try again.";
        setErrorMessage(message);
        toast.error(message);
      },
    });

  // Mutation for saving a joke
  const saveJokeMutation =
    trpcReactQueryApi.main.dadJokes.saveJoke.useMutation({
      onSuccess: async () => {
        setErrorMessage(null);
        toast.success("Joke saved to your library!");
        await utils.main.dadJokes.getSavedJokes.invalidate();
      },
      onError: (error) => {
        const message = error.message || "Failed to save joke";
        setErrorMessage(message);
        toast.error(message);
      },
    });

  // Mutation for deleting a saved joke
  const deleteSavedJokeMutation =
    trpcReactQueryApi.main.dadJokes.deleteSavedJoke.useMutation({
      onSuccess: async () => {
        setErrorMessage(null);
        toast.success("Joke removed from library");
        await utils.main.dadJokes.getSavedJokes.invalidate();
      },
      onError: (error) => {
        const message = error.message || "Failed to delete joke";
        setErrorMessage(message);
        toast.error(message);
      },
    });

  // Auto-load saved jokes on mount if enabled and authenticated
  useEffect(() => {
    if (autoLoadLibrary && isAuthenticated) {
      void refetchSavedJokes();
    }
  }, [autoLoadLibrary, isAuthenticated, refetchSavedJokes]);

  // Commands
  const loadSavedJokes = useCallback(() => {
    if (!isAuthenticated) {
      setErrorMessage("You must be logged in to view your library");
      toast.error("Please log in to view your saved jokes");
      return;
    }
    void refetchSavedJokes();
  }, [isAuthenticated, refetchSavedJokes]);

  const buildShareUrl = useCallback((jokeText: string): string => {
    const encodedText = encodeURIComponent(jokeText);
    const hashtag = encodeURIComponent("#DadJoke");
    return `https://twitter.com/intent/tweet?text=${encodedText}&hashtags=${hashtag}`;
  }, []);

  // Mutations
  const generateJoke = useCallback(
    () => ({
      mutate: () => {
        // Pass the exclusion list to the server
        generateJokeMutation.mutate({ excludeJokes: seenJokes });
      },
      loading: generateJokeMutation.isPending,
      error: generateJokeMutation.error?.message ?? null,
    }),
    [generateJokeMutation, seenJokes],
  );

  const saveJoke = useCallback(
    () => ({
      mutate: (jokeText: string) => {
        if (!isAuthenticated) {
          setErrorMessage("You must be logged in to save jokes");
          toast.error("Please log in to save jokes");
          return;
        }
        saveJokeMutation.mutate({ jokeText });
      },
      loading: saveJokeMutation.isPending,
      error: saveJokeMutation.error?.message ?? null,
    }),
    [saveJokeMutation, isAuthenticated],
  );

  const deleteSavedJoke = useCallback(
    () => ({
      mutate: (savedJokeId: string) => {
        deleteSavedJokeMutation.mutate({ id: savedJokeId });
      },
      loading: deleteSavedJokeMutation.isPending,
      error: deleteSavedJokeMutation.error?.message ?? null,
    }),
    [deleteSavedJokeMutation],
  );

  return {
    // State
    currentJoke,
    savedJokes: savedJokesData ?? [],
    isGenerating: generateJokeMutation.isPending,
    isLoadingLibrary,
    errorMessage,
    isAuthenticated,

    // Commands
    loadSavedJokes,
    buildShareUrl,

    // Mutations
    generateJoke,
    saveJoke,
    deleteSavedJoke,
  };
}